import { MarkerGroup } from 'app/common/components/geo-manager/geo-manager.interfaces';

import {
  RouteMapFeature,
  RouteMapFeatureCollection,
} from './route-optimization.interface';

/**
 * Del `geojson` de `POST /route-map` al modelo que el mapa pinta.
 *
 * **Este módulo es el único lugar donde se invierten las coordenadas.** El
 * servicio devuelve `[lon, lat]` (estándar GeoJSON) y Leaflet espera
 * `[lat, lon]`; el `CLAUDE.md` de ave-routes señala ese cruce como la fuente de
 * bugs más común del stack. Concentrarlo acá lo vuelve una regla probada en vez
 * de una convención que cada consumidor recuerda o no.
 *
 * Módulo puro: entra el geojson, sale el modelo de vista. Sin HTTP, sin Angular.
 */

/** Trazo de una ruta, en el orden que Leaflet necesita. */
export interface RoutePolyline {
  truckId: string;
  color: string;
  /** Puntos `[lat, lon]`. */
  points: [number, number][];
}

/** Lo que el mapa necesita para una selección concreta. */
export interface RouteMapView {
  markerGroups: MarkerGroup[];
  polylines: RoutePolyline[];
}

/**
 * Datos del cliente de una parada, para el marcador del mapa.
 *
 * El `geojson` del servicio solo trae `order_id`, `sequence`, `arrival_time` y el
 * color: el nombre del cliente, su dirección, su territorio y su peso viven del
 * lado del cliente, en la parada que ya se construyó para la lista. Se **inyecta**
 * (ver el parámetro `pointOf` de `routeMapView`) en vez de resolverse acá porque
 * este módulo no conoce las órdenes, y no tiene por qué: su trabajo es el mapa.
 *
 * `StopRow` lo satisface estructuralmente, así que el consumidor pasa las paradas
 * que ya tiene sin construir nada nuevo.
 */
export interface MapPointInfo {
  name: string;
  /**
   * Nombre comercial del cliente. Va como propiedad ROTULADA del popup y no en el
   * título: ese ya lleva la secuencia y el par código–nombre, y una línea más larga
   * ahí se corta antes que el dato que identifica la parada.
   */
  commercialName?: string;
  address: string;
  territory?: string;
  routeName?: string;
  /**
   * Serial del documento (el "número de pedido" que el operador conoce). Es lo
   * que el popup muestra como `Pedido:`; sin él, el renglón se OMITE — un id de
   * Mongo ahí no le dice nada a nadie.
   */
  serial?: string;
  weightLb?: number;
  arrivalTime?: string;
  departureTime?: string;
}

/** Nombre del grupo del almacén en la leyenda del mapa. */
export const WAREHOUSE_GROUP_NAME = 'Almacén';

/** Color del marcador del almacén (slate-900: destaca sobre la paleta). */
export const WAREHOUSE_MARKER_COLOR = '#0f172a';

/** Color de respaldo cuando el servicio no asignó uno. */
const FALLBACK_COLOR = '#6366f1';

const isStop = (f: RouteMapFeature): boolean => f.properties?.kind === 'stop';
const isRoute = (f: RouteMapFeature): boolean => f.properties?.kind === 'route';
const isWarehouse = (f: RouteMapFeature): boolean => f.properties?.kind === 'warehouse';

/**
 * Renglón de horas del marcador: llegada y salida cuando se conocen.
 *
 * La llegada la trae el `geojson`; la salida solo la parada del lado del
 * cliente. Con una sola de las dos el renglón se arma igual — media respuesta
 * sigue siendo mejor que ninguna.
 */
function markerSubtitle(arrivalTime: string | undefined, info?: MapPointInfo): string | undefined {
  const arrival = arrivalTime || info?.arrivalTime;
  if (!arrival) return undefined;
  return info?.departureTime ? `Llegada ${arrival} · sale ${info.departureTime}` : `Llegada ${arrival}`;
}

/**
 * Datos adicionales del marcador, con las llaves como rótulos.
 *
 * El GeoManager pinta `customProperties` como pares `llave: valor`, así que la
 * llave ES la etiqueta que se lee: van en español y capitalizadas, igual que en
 * el resto de la pantalla.
 *
 * Se omite lo que no se puede afirmar: un "Territorio: undefined" en el popup es
 * peor que un renglón menos. `Pedido:` lleva el SERIAL del documento — el número
 * que el operador conoce y puede cruzar con el maestro — y sin serial el renglón
 * se omite: un id de Mongo ahí no cruza con nada humano.
 */
function markerProperties(info?: MapPointInfo): Record<string, string> | undefined {
  const props: Record<string, string> = {};
  if (info?.commercialName) props['Nombre comercial'] = info.commercialName;
  if (info?.routeName) props['Ruta'] = info.routeName;
  if (info?.territory) props['Territorio'] = info.territory;
  if (info?.weightLb) props['Peso'] = `${info.weightLb.toFixed(1)} lb`;
  if (info?.serial) props['Pedido'] = info.serial;
  return Object.keys(props).length ? props : undefined;
}

/** `[lon, lat]` → `[lat, lon]`. La inversión, en un solo lugar. */
function toLatLon(coordinates: [number, number]): [number, number] {
  const [lon, lat] = coordinates;
  return [lat, lon];
}

/**
 * Compone el mapa para una selección.
 *
 * @param geojson respuesta del servicio; tolera `undefined` (grupo que falló).
 * @param truckId ruta a aislar, o `null` para ver todas.
 *
 * Con una ruta aislada se devuelve su trazo; con **todas**, ninguno: son
 * decenas de miles de puntos de línea simultáneos, y esa vista sirve para
 * ubicar paradas, no para seguir un trayecto.
 *
 * El almacén se incluye **siempre**: toda ruta sale de ahí y vuelve ahí, así que
 * sin él una ruta aislada no se interpreta.
 *
 * @param pointOf datos del cliente de una parada, por `order_id`. Opcional: sin
 *   él el pin se rotula con el id de Mongo del pedido, que no le dice nada a
 *   nadie. Con él, el marcador lleva nombre, dirección, ruta, territorio y peso,
 *   que es lo que el operador necesita al hacer clic sobre un punto.
 */
export function routeMapView(
  geojson: RouteMapFeatureCollection | undefined,
  truckId: string | null,
  pointOf?: (orderId: string) => MapPointInfo | undefined,
): RouteMapView {
  const features = geojson?.features ?? [];
  if (!features.length) return { markerGroups: [], polylines: [] };

  const wanted = (f: RouteMapFeature): boolean =>
    truckId === null || f.properties?.truck_id === truckId;

  // Una parada por marcador, agrupada por camión para que comparta color.
  const byTruck = new Map<string, MarkerGroup>();
  for (const f of features) {
    if (!isStop(f) || !wanted(f) || f.geometry?.type !== 'Point') continue;
    const id = f.properties.truck_id ?? '';
    const [latitude, longitude] = toLatLon(f.geometry.coordinates);
    const orderId = f.properties.order_id ?? '';
    const info = pointOf?.(orderId);
    const group = byTruck.get(id) ?? {
      // El nombre de la ENTREGA, no el `truck_id`: en las pantallas que
      // particionan por sub-ruta ese id es la llave del grupo
      // (`sub-route:<mongoId>`) y aparecía al pie del popup. Todas las paradas
      // del grupo comparten `routeName`, así que el de la primera lo nombra.
      groupName: info?.routeName || id,
      groupColor: f.properties.color ?? FALLBACK_COLOR,
      markers: [],
    };
    group.markers.push({
      // El nombre del cliente cuando se puede saber; el id, solo como respaldo.
      title: `${f.properties.sequence ?? ''}. ${info?.name || orderId}`.trim(),
      subtitle: markerSubtitle(f.properties.arrival_time, info),
      // La dirección va en `description` porque es lo que el popup del
      // GeoManager pinta en ese renglón; no hay campo propio para ella.
      description: info?.address || undefined,
      latitude,
      longitude,
      // El `order` es lo que hace que el pin salga numerado; el almacén no lo lleva.
      order: f.properties.sequence,
      customProperties: markerProperties(info),
    });
    byTruck.set(id, group);
  }

  const warehouses: MarkerGroup[] = features
    .filter((f) => isWarehouse(f) && f.geometry?.type === 'Point')
    .map((f) => {
      const [latitude, longitude] = toLatLon(
        (f.geometry as { coordinates: [number, number] }).coordinates,
      );
      return {
        groupName: WAREHOUSE_GROUP_NAME,
        groupColor: WAREHOUSE_MARKER_COLOR,
        // Sin `order`: pin sólido sin número, distinto de las paradas.
        markers: [{ title: 'Almacén', subtitle: 'Salida y regreso', latitude, longitude }],
      };
    });

  const polylines: RoutePolyline[] =
    truckId === null
      ? []
      : features
          .filter((f) => isRoute(f) && wanted(f) && f.geometry?.type === 'LineString')
          .map((f) => ({
            truckId: f.properties.truck_id ?? '',
            color: f.properties.color ?? FALLBACK_COLOR,
            points: (f.geometry as { coordinates: [number, number][] }).coordinates.map(toLatLon),
          }))
          // Una LineString vacía no es una línea: dibujarla no aporta y confunde al encuadre.
          .filter((p) => p.points.length > 0);

  return { markerGroups: [...warehouses, ...byTruck.values()], polylines };
}
